import React from 'react';
import styles from './RouteListCustomers.module.scss';
import useExternalApiService from 'helpers/ExternalApiService';
import { store } from 'stores/Store';
import { ReactComponent as UserIcon } from "assets/user2.svg";
import { ReactComponent as LeadIcon } from "assets/UserPlus.svg";
import { ReactComponent as  MagnifyingGlassIcon } from 'assets/magnifyingGlass.svg';
import { useNavigate } from 'react-router-dom';
import Button from "components/Button/Button";
import { Loader } from '@mantine/core';

interface IRouteListCustomersProps {
  onCustomerAdd: (customer: any) => void;
  onSelectionChange?: (selectedPractitioners: any[]) => void;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
}

const RouteListCustomers = ({ onCustomerAdd, onSelectionChange, searchTerm, setSearchTerm }: IRouteListCustomersProps) => {
  const [practitioners, setPractitioners] = React.useState<any[]>([]);  
  const [selectedPractitioners, setSelectedPractitioners] = React.useState<any[]>([]);
  const [renderKey, setRenderKey] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  
  // Debounced search term for API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState(searchTerm);
  
  const LIMIT = 50;
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);
  const navigate = useNavigate();

  const prevSearchTermRef = React.useRef<string | null>(null);

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  React.useEffect(() => {
    const currentGPSVerifiable = store.getPractitionersCarouselFilter() == "Geo-Verified";
    const currentSearchTerm = store.getCachedSearchTerm();
    if (!currentGPSVerifiable || currentSearchTerm || store.getPractitionersFilterMultiSelectValue().length < 6) {
      store.setPractitionersCarouselFilter("Geo-Verified");
      store.setCachedSearchTerm("");
      store.setPractitionersFilterMultiSelectValue(["Name", "Address", "City", "State", "Specialty", "Email", "Zipcode"]);
      store.clearPractitionersCache();
    }
  }, []);

  // 1) Reset practitioners list when searchTerm changes
  React.useEffect(() => {
    if (prevSearchTermRef.current !== null && prevSearchTermRef.current !== debouncedSearchTerm) {
      console.log('RouteListCustomers: Clearing cache due to search term change:', prevSearchTermRef.current, '->', debouncedSearchTerm);
      setPractitioners([]);
      setPage(1);
      setHasMore(true);
      store.clearPractitionersCache();
    }
    prevSearchTermRef.current = debouncedSearchTerm;
  }, [debouncedSearchTerm]);

  // 2) Fetch practitioners when page or searchTerm changes
  React.useEffect(() => {
    const controller = new AbortController();

    const fetchPractitioners = async () => {
      // Only block pagination requests during loading, not new searches
      if (page > 1 && isLoading) return;
      
      const cachedPractitioners = store.getPractitionersCache(page);
      console.log('RouteListCustomers: Checking cache for page:', page, 'cached:', !!cachedPractitioners, 'cache size:', Object.keys(store.practitionersCache).length);
      if (cachedPractitioners) {
        console.log('RouteListCustomers: Using cached data for page:', page);
        if (page === 1) {
          setPractitioners(cachedPractitioners);
        } else {
          setPractitioners((prev: any[]) => [...prev, ...cachedPractitioners]);
        }
        return;
      }

      console.log('RouteListCustomers: Cache miss for page:', page, 'making API call');
      //ugly fix
      let toggle = true;

      setIsLoading(true);
      try {
        const data = await postData('/api/get_practitioners_for_chat', {
          "fmc_email": store.impersonatedUser,
          "search": debouncedSearchTerm,
          "page": page,
          "limit": LIMIT,
          "onlyGeoValidated": true,
          "carouselFilter": "Geo-Verified",
          "alias": store.impersonatedUserAlias,
        }, controller.signal);

        console.log('RouteListCustomers: Caching data for page:', page, 'data length:', data.practitioners?.length);
        store.setPractitionersCache(page, data.practitioners || []);

        if (page === 1) {
          setPractitioners(data.practitioners || []);
        } else {
          setPractitioners((prev: any[]) => [...prev, ...(data.practitioners || [])]);
        }

        if (!data.practitioners || data.practitioners.length < LIMIT) {
          setHasMore(false); // No more pages
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Error fetching practitioners:', error);
        } else {
          toggle = false;
        }
      } finally {
        if (toggle) {
          setIsLoading(false);
        }
        
      }
    };

    fetchPractitioners();

    return () => controller.abort();
  }, [page, debouncedSearchTerm]);

  // 3) Intersection Observer to load next page when sentinel is visible
  const observer = React.useRef<IntersectionObserver | null>(null);
  const lastPractitionerRef = React.useCallback((node: HTMLDivElement | null) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  const togglePractitionerSelection = (practitioner: any) => {
    const isSelected = selectedPractitioners.some(p => p.id === practitioner.id);
    if(isSelected) {
      setSelectedPractitioners(selectedPractitioners.filter(p => p.id !== practitioner.id));
    } else {
      setSelectedPractitioners([...selectedPractitioners, practitioner]);
    }
    setRenderKey(prev => prev + 1);
  }

  React.useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedPractitioners);
    }
  }, [selectedPractitioners, onSelectionChange]);

  const renderTabContent = () => {
    // No additional filtering needed (backend handles it)
    return practitioners;
  }

  const renderHeader = () => {
    return (
      <div className={styles.headerWrapper}>
        <p className={styles.headerText}>{selectedPractitioners.length} Practitioners Selected</p>
        <div className={styles.headerButtons}>
          {selectedPractitioners.length > 0 && (
            <>
              <Button type="outline" onClick={() => {setSelectedPractitioners([]); setRenderKey(prev => prev + 1);}} >CLEAR</Button>
              <Button type="outline" onClick={() => {onCustomerAdd(selectedPractitioners); setSelectedPractitioners([]); setRenderKey(prev => prev + 1);}} >ADD</Button>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.wrapper} key={renderKey}>      
      {renderHeader()}
      <div className={styles.searchWrapper}>
        <MagnifyingGlassIcon width={20} height={20} />        
        <input
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
        {searchTerm && (
          <button 
            className={styles.clearButton}
            onClick={() => setSearchTerm('')}
          >
            X
          </button>
        )}        
      </div>
      <p className={styles.infoText}>Displaying Accounts with Valid Lat. & Long.</p>
      {renderTabContent().map((customer: any, index: number) => {
        const isNearBottom = index > practitioners.length - 50;
        return (
          <div
            ref={isNearBottom ? lastPractitionerRef : undefined}
            onClick={() => {togglePractitionerSelection(customer)}}
            className={`${styles.customerEdit} ${selectedPractitioners.some(p => p.id === customer.id) ? styles.customerEditSelected : ''}`}
            key={customer.id}
          >
            <div className={`${styles.radioDiv} ${selectedPractitioners.some(p => p.id === customer.id) ? styles.radioDivSelected : ''}`} />
            <div>
              <p className={styles.practitionerName}>{`${index + 1}. ${customer.primary_name}`}</p>          
              <p className={styles.category}>{customer.specialty}</p>
            </div>
          </div>
        );
      })}
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
          <Loader size="lg" />
        </div>
      )}
    </div>
  )
}

export default RouteListCustomers;
